import type { KnownError } from '../../utils/api';

// Grabs the flat {"code":…,"message":…} gatewayResponse object embedded in a fault string
// so JSON.parse can read it. [^{}]* ends the match at that object's own closing brace, so
// a fault chaining several causes gives the first gateway response rather than a span
// across two of them.
//
// in:  order.message, e.g. 'failed to execute order with existing account "A00108489":
//      failed to parse create order method response: unsuccessful request execution for
//      "createOrder": 58560099: {"gatewayResponse":{"code":"402","message":
//      "[card_error/card_declined/generic_decline] Your card was declined."}};'
// out: '[card_error/card_declined/generic_decline] Your card was declined.'
const GATEWAY_RESPONSE = /"gatewayResponse"\s*:\s*(\{[^{}]*\})/;

export function extractGatewayMessage(message: string): string {
  const match = GATEWAY_RESPONSE.exec(message);

  if (!match) {
    return '';
  }

  try {
    const gatewayResponse = JSON.parse(match[1]);
    return gatewayResponse.message?.trim() ?? '';
  } catch {
    return '';
  }
}

/**
 * The order endpoint also fails when the payment gateway turns the payment down
 * (declined card, insufficient funds, ...). That's not a bug on our side, so
 * those get their own low-priority Sentry issue instead of sharing one with
 * real order failures.
 */
export function getPaymentDecline(fault: object | null): KnownError | null {
  const message =
    fault && 'message' in fault && typeof fault.message === 'string'
      ? extractGatewayMessage(fault.message)
      : '';

  return message
    ? {
        title: 'Payment declined',
        tags: { 'payment.gateway_message': message },
      }
    : null;
}
